// Meetings — inbound calendar invites attributed to the outreach chain.
//
// Interactive controls:
//   - Click any row → /inbox/:id (existing reply detail view) with full body + thread
//   - Attribution toggle (default: Attributed only — the metric that matters)
//   - Search box: live client-side filter on creator name + from email/name
//   - Pagination (50/page) with total count, same UX as engagement activity feed

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMeetingsStats, useMeetingsRecent } from '../lib/queries';

const PAGE_SIZE = 50;

export function Meetings() {
  const navigate = useNavigate();
  const [attributedOnly, setAttributedOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const { data: stats, isLoading: statsLoading } = useMeetingsStats();
  const { data: pageData, isFetching } = useMeetingsRecent(
    page * PAGE_SIZE,
    PAGE_SIZE,
    attributedOnly,
  );

  const rows = pageData?.rows ?? [];
  const total = pageData?.total ?? 0;
  const first = rows.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const last = page * PAGE_SIZE + rows.length;
  const hasNext = last < total;
  const hasPrev = page > 0;

  // Client-side search across the current page. Keeps server simple; the
  // operator hits Next/Prev to expand the search range if needed.
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.creator_name ?? '',
        r.from_email ?? '',
        r.from_name ?? '',
        r.subject ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const headlineMetric = attributedOnly
    ? stats?.total_attributed ?? 0
    : stats?.total_all ?? 0;
  const headlineSub = attributedOnly
    ? stats?.last_7d_attributed ?? 0
    : stats?.last_7d_all ?? 0;

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section D</span>
          <span>Meetings</span>
          <span>{stats ? `${stats.total_attributed.toLocaleString()} attributed` : '…'}</span>
        </div>
        <h1 className="page__title">Meeting invites</h1>
        <p className="page__deck">
          Inbound calendar invites detected on reply threads. The headline counter follows the
          attribution toggle. Click any row to open the full email + the outreach chain it
          replied to.
        </p>
      </header>

      <section className="meetings__stats">
        <div className="meetings__stat meetings__stat--primary">
          <div className="meetings__stat-num">
            {statsLoading ? '…' : headlineMetric.toLocaleString()}
          </div>
          <div className="meetings__stat-label">
            {attributedOnly ? 'Attributed to outreach (lifetime)' : 'All meeting invites (lifetime)'}
          </div>
          <div className="meetings__stat-sub">
            {statsLoading ? '…' : `${headlineSub.toLocaleString()} this week`}
          </div>
        </div>
        <div className="meetings__stat">
          <div className="meetings__stat-num">
            {statsLoading
              ? '…'
              : attributedOnly
                ? (stats?.total_all ?? 0).toLocaleString()
                : (stats?.total_attributed ?? 0).toLocaleString()}
          </div>
          <div className="meetings__stat-label">
            {attributedOnly ? 'All (incl. unattributed)' : 'Attributed only'}
          </div>
          <div className="meetings__stat-sub">
            {statsLoading
              ? '…'
              : `${(attributedOnly ? stats?.last_7d_all ?? 0 : stats?.last_7d_attributed ?? 0).toLocaleString()} this week`}
          </div>
        </div>
        <div className="meetings__stat">
          <div className="meetings__stat-num">
            {stats?.last_received_at
              ? new Date(stats.last_received_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : '—'}
          </div>
          <div className="meetings__stat-label">Most recent</div>
        </div>
      </section>

      <section className="meetings__list">
        <header className="meetings__list-head">
          <h2 className="meetings__list-title">Recent meeting invites</h2>
          <div className="meetings__controls">
            <input
              type="search"
              className="meetings__search"
              placeholder="Search creator, email, subject…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="meetings__toggle">
              <button
                type="button"
                className={`meetings__toggle-btn ${attributedOnly ? 'meetings__toggle-btn--active' : ''}`}
                onClick={() => {
                  setAttributedOnly(true);
                  setPage(0);
                }}
              >
                Attributed
              </button>
              <button
                type="button"
                className={`meetings__toggle-btn ${!attributedOnly ? 'meetings__toggle-btn--active' : ''}`}
                onClick={() => {
                  setAttributedOnly(false);
                  setPage(0);
                }}
              >
                All
              </button>
            </div>
            <span className="meetings__page-info">
              {total > 0
                ? `${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()}`
                : 'No meeting invites match.'}
              {isFetching && rows.length > 0 ? ' · refreshing…' : ''}
            </span>
            {total > PAGE_SIZE && (
              <div className="meetings__pager">
                <button
                  type="button"
                  className="meetings__pager-btn"
                  disabled={!hasPrev}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  ← Newer
                </button>
                <button
                  type="button"
                  className="meetings__pager-btn"
                  disabled={!hasNext}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Older →
                </button>
              </div>
            )}
          </div>
        </header>
        {visibleRows.length > 0 ? (
          <table className="meetings__table">
            <thead>
              <tr>
                <th>Received</th>
                <th>Creator</th>
                <th>From</th>
                <th>Subject</th>
                <th>Attributed</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => (
                <tr
                  key={r.id}
                  className="meetings__row meetings__row--clickable"
                  onClick={() => navigate(`/inbox/${r.id}`)}
                  title="Open full email + thread"
                >
                  <td className="meetings__when">
                    {new Date(r.received_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    })}
                  </td>
                  <td>{r.creator_name ?? <span className="meetings__muted">—</span>}</td>
                  <td className="meetings__from">
                    {r.from_name ? `${r.from_name} ` : ''}
                    <span className="meetings__email">&lt;{r.from_email}&gt;</span>
                  </td>
                  <td className="meetings__subject">
                    {r.subject ?? <span className="meetings__muted">—</span>}
                  </td>
                  <td>
                    {r.in_reply_to_email_id ? (
                      <span className="meetings__badge meetings__badge--yes">YES</span>
                    ) : (
                      <span className="meetings__badge meetings__badge--no">no</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="meetings__empty">
            {search
              ? `No matches for "${search}" on this page. Try Older → to expand search.`
              : attributedOnly
                ? 'No attributed meeting invites yet. They appear here as creators reply with calendar bookings.'
                : 'No meeting invites yet.'}
          </div>
        )}
      </section>
    </div>
  );
}
